import { useEffect, useRef, useState } from 'react';

const DEFAULT_OPTIONS = {
  threshold: 0.15,
  rootMargin: '0px 0px -8% 0px',
  repeat: false,
};

function isInViewport(node, viewportHeight) {
  const rect = node.getBoundingClientRect();
  return rect.top < viewportHeight * 0.92 && rect.bottom > viewportHeight * 0.08;
}

function triggerReveal(setIsVisible) {
  setIsVisible(false);

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      setIsVisible(true);
    });
  });
}

export const useScrollReveal = (options = {}) => {
  const { threshold, rootMargin, repeat } = { ...DEFAULT_OPTIONS, ...options };
  const wrapperRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return undefined;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      setIsVisible(true);
      return undefined;
    }

    let observer = null;
    let cancelled = false;

    const reveal = () => {
      if (cancelled) return;

      if (repeat) {
        triggerReveal(setIsVisible);
        return;
      }

      setIsVisible(true);
    };

    const hide = () => {
      if (!cancelled) {
        setIsVisible(false);
      }
    };

    const setupObserver = () => {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            reveal();

            if (!repeat) {
              observer.disconnect();
              observer = null;
            }

            return;
          }

          if (repeat) {
            hide();
          }
        },
        { threshold, rootMargin },
      );

      observer.observe(node);
    };

    if (repeat) {
      setupObserver();

      return () => {
        cancelled = true;
        observer?.disconnect();
      };
    }

    const viewportHeight = window.innerHeight;
    const inInitialViewport = isInViewport(node, viewportHeight);

    if (inInitialViewport) {
      const onFirstScroll = () => {
        window.removeEventListener('scroll', onFirstScroll);

        if (cancelled) return;

        if (isInViewport(node, window.innerHeight)) {
          reveal();
          return;
        }

        setupObserver();
      };

      window.addEventListener('scroll', onFirstScroll, { passive: true });

      return () => {
        cancelled = true;
        window.removeEventListener('scroll', onFirstScroll);
        observer?.disconnect();
      };
    }

    setupObserver();

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [threshold, rootMargin, repeat]);

  return { wrapperRef, isVisible };
};
